import React, { lazy } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { AppViewRouterProps, EMPTY_METADATA, DEFAULT_INBOX_ITEMS, DEFAULT_PROCESSED_ITEMS } from './types';
import { isCommProject, isSystemProject, filterAndSortRealProjects } from '../../utils/idNormalizer';

const AIAssistantHubView = lazy(() => import('../AIAssistantHubView'));
const GlobalGanttView = lazy(() => import('../GlobalGanttView'));
const ImpactEffortMatrixView = lazy(() => import('../ImpactEffortMatrixView'));
const CommunicationView = lazy(() => import('../CommunicationView'));
const ContactGraphView = lazy(() => import('../ContactGraphView'));
const GlobalKanbanView = lazy(() => import('../GlobalKanbanView'));
const ChangelogView = lazy(() => import('../ChangelogView'));

export const WorkspaceCollaborationRouter: React.FC<AppViewRouterProps> = (props) => {
  const {
    isProjectEditor,
    workspaceProjects,
    handleAddTask,
    addIdea,
    todayStr,
    droppedZoneId,
    activeDragId,
    registerViewDragHandlers,
    handleReorderTask,
    hiddenProjectIds,
    dropdownProjects,
    activeAnalysisTab,
    setActiveAnalysisTab,
    selectedAnalysisProjectId,
    setSelectedAnalysisProjectId,
    globalAllAssignees,
    aiAssistantTab,
    setAiAssistantTab,
    handleAddToHistory,
  } = props;

  const projectCtx = useProject();
  const {
    activeProject,
    currentProjectId,
    setCurrentProjectId,
    updateTask,
    updateTasksMultiple,
    deleteTask,
    updateProjectMetadata,
    allProjects,
    allTasksGlobal,
    activeWorkspace,
    addProject,
    changelog,
    setChangelog,
  } = projectCtx;

  const uiCtx = useUI();
  const {
    viewMode,
    searchQuery,
    setSearchQuery,
    searchMode,
    searchMatchIds,
    searchResults,
    searchIndex,
    activeSearchMatchId,
    kanbanMode,
    showArchived,
    showCompleted,
    isDarkMode,
    setEditingTask,
  } = uiCtx;

  const exportKanbanColumnAsExcel = props.exportKanbanColumnAsExcel ?? projectCtx.exportKanbanColumnAsExcel;
  const exportContactsAsExcel = props.exportContactsAsExcel ?? projectCtx.exportContactsAsExcel;
  const syncInboxForce = props.syncInboxForce ?? projectCtx.syncInboxForce;
  const handleAddAITasks = props.handleAddAITasks ?? projectCtx.handleAddAITasks;

  const communicationTab = props.communicationTab ?? uiCtx.communicationTab;
  const setCommunicationTab = props.setCommunicationTab ?? uiCtx.setCommunicationTab;
  const selectedInboxItemId = props.selectedInboxItemId !== undefined ? props.selectedInboxItemId : uiCtx.selectedInboxItemId;
  const setSelectedInboxItemId = props.setSelectedInboxItemId ?? uiCtx.setSelectedInboxItemId;
  const activeProjectTab = props.activeProjectTab ?? uiCtx.activeProjectTab;
  const filterAssignee = props.filterAssignee ?? uiCtx.filterAssignee;
  const filterStatus = props.filterStatus ?? uiCtx.filterStatus;
  const filterType = props.filterType ?? uiCtx.filterType;
  const filterPriority = props.filterPriority ?? uiCtx.filterPriority;

  return (
    <>
      {/* AI Assistant View */}
      {viewMode === 'aiAssistant' && (
        <AIAssistantHubView 
          projects={workspaceProjects} 
          onAddProject={addProject}
          onAddTasks={handleAddAITasks}
          onAddToHistory={handleAddToHistory}
          inboxItems={allProjects.find(p => p.id === 'inbox_inforum')?.metadata?.inboxItems || DEFAULT_INBOX_ITEMS}
          processedItems={allProjects.find(p => p.id === 'inbox_inforum')?.metadata?.processedItems || DEFAULT_PROCESSED_ITEMS}
          globalMetadata={allProjects.find(p => p.id === 'inbox_inforum')?.metadata || EMPTY_METADATA}
          onUpdateGlobalMetadata={(updates) => updateProjectMetadata('inbox_inforum', updates)}
          onUpdateProjectMetadata={updateProjectMetadata}
          onSync={syncInboxForce}
          onResetTestData={() => {
            if (window.confirm("Möchten Sie die Standard-Testdaten der Mobile Inbox wiederherstellen?")) {
              updateProjectMetadata('inbox_inforum', { 
                inboxItems: DEFAULT_INBOX_ITEMS, 
                processedItems: DEFAULT_PROCESSED_ITEMS 
              });
            }
          }}
          activeTab={aiAssistantTab}
          setActiveTab={setAiAssistantTab}
          activeWorkspace={activeWorkspace}
          selectedInboxItemId={selectedInboxItemId}
          setSelectedInboxItemId={setSelectedInboxItemId}
          searchMatchIds={searchMatchIds}
          activeSearchMatchId={activeSearchMatchId}
          addIdea={addIdea}
        />
      )}

      {/* Global Gantt View */}
      {viewMode === 'globalGantt' && (
        <GlobalGanttView />
      )}

      {/* Analysis View */}
      {viewMode === 'analysis' && (
        <ImpactEffortMatrixView 
          projects={dropdownProjects} 
          onUpdateProject={(p) => updateProjectMetadata(p.id, p.metadata || {})} 
          isDarkMode={isDarkMode}
          activeTab={activeAnalysisTab}
          onTabChange={setActiveAnalysisTab}
          selectedProjectId={selectedAnalysisProjectId}
          onProjectChange={setSelectedAnalysisProjectId}
        />
      )}

      {/* Communication Views */}
      {viewMode === 'communication' && communicationTab !== 'network' && (
        <CommunicationView 
          showArchived={showArchived}
          showCompleted={showCompleted}
          projects={[
            ...allProjects.filter(p => isCommProject(p.id)),
            ...workspaceProjects.filter(p => !isCommProject(p.id))
          ]}
          projectId={undefined}
          activeTab={communicationTab}
          onTabChange={setCommunicationTab}
          activeWorkspace={activeWorkspace}
          onTaskUpdate={(id, updates, projId) => {
            if (id === 'new') {
              const targetId = updates.projectId || projId || 'comm_internal';
              const activeRealProjects = filterAndSortRealProjects(workspaceProjects, { activeWorkspace });
              const defaultProjectIds =
                Array.isArray(updates.projectIds) && updates.projectIds.length > 0
                  ? updates.projectIds.filter((pid: string) => !isSystemProject(pid))
                  : !isSystemProject(targetId)
                    ? [targetId]
                    : activeRealProjects.length > 0
                      ? [activeRealProjects[0].id]
                      : [];
              const newTask = handleAddTask(updates.type || 'contact', undefined, targetId, '', {
                ...updates,
                projectIds: defaultProjectIds,
                category: 'Kommunikation',
                workspace: activeWorkspace,
                isInlineNew: true
              });
              if (newTask) setEditingTask(newTask);
            } else {
              updateTask(id, updates, projId);
            }
          }}
          onAddTask={handleAddTask}
          onTaskDelete={deleteTask}
          onTaskSelect={(task) => setEditingTask(task)}
          onTaskUpdateMultiple={updateTasksMultiple}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchMatchIds={searchMatchIds}
          searchResults={searchResults}
          searchIndex={searchIndex}
          activeSearchMatchId={activeSearchMatchId}
          searchMode={searchMode}
          allAssignees={globalAllAssignees}
          exportContactsAsExcel={exportContactsAsExcel}
          activeDragId={activeDragId}
          onRegisterDragHandlers={registerViewDragHandlers}
          onTaskReorder={handleReorderTask}
        />
      )}

      {viewMode === 'communication' && communicationTab === 'network' && (
        <div className="flex-1 w-full bg-slate-50 dark:bg-slate-950 overflow-hidden relative isolate h-full p-4">
          <ContactGraphView 
             projects={allProjects.filter(p => (p.workspace || 'professional') === activeWorkspace)}
             isDarkMode={isDarkMode}
             globalMetadata={allProjects.find(p => p.id === 'inbox_inforum')?.metadata || EMPTY_METADATA}
             onUpdateGlobalMetadata={(updates) => updateProjectMetadata('inbox_inforum', updates)}
             onTaskUpdate={updateTask}
             onAddToHistory={handleAddToHistory}
             searchMatchIds={searchMatchIds}
             searchResults={searchResults}
             searchIndex={searchIndex}
             activeSearchMatchId={activeSearchMatchId}
          />
        </div>
      )}

      {/* Changelog View */}
      {viewMode === 'changelog' && (
        <ChangelogView 
          changelog={changelog}
          onClear={() => setChangelog([])}
          project={activeProject || undefined}
          allProjects={workspaceProjects}
          onTaskClick={(taskId, pId) => {
            const t = allTasksGlobal.find(x => String(x.id) === String(taskId));
            if (t) {
              if (pId && pId !== currentProjectId) setCurrentProjectId(pId);
              setEditingTask(t);
            }
          }}
        />
      )}

      {/* Global Kanban View */}
      {viewMode === 'globalKanban' && (
        <GlobalKanbanView 
          projects={[
            ...workspaceProjects.filter(p => {
              if (activeProjectTab === 'active' && p.archived) return false;
              if (activeProjectTab === 'archived' && !p.archived) return false;
              return !hiddenProjectIds.includes(p.id) && !isCommProject(p.id);
            }),
            ...allProjects.filter(p => isCommProject(p.id))
          ]}
          currentUserId={props.currentUserId}
          userEmail={props.userEmail}
          onTaskUpdate={updateTask}
          onTaskUpdateMultiple={updateTasksMultiple}
          onTaskDelete={deleteTask}
          onTaskSelect={(task) => {
            const project = allProjects.find(p => (p.tasks || []).some(t => String(t.id) === String(task.id)));
            setEditingTask({ ...task, projectId: project?.id || 'inbox_inforum' });
          }}
          onAddTask={handleAddTask}
          onUpdateMetadata={updateProjectMetadata}
          onTaskReorder={handleReorderTask}
          todayStr={todayStr}
          searchQuery={searchQuery}
          mode={kanbanMode === 'custom' ? 'status' : kanbanMode as any}
          filterAssignee={filterAssignee}
          filterStatus={filterStatus}
          filterType={filterType}
          filterPriority={filterPriority}
          allAssignees={globalAllAssignees}
          searchMatchIds={searchMatchIds}
          searchResults={searchResults}
          searchIndex={searchIndex}
          activeSearchMatchId={activeSearchMatchId}
          searchMode={searchMode}
          activeDragId={activeDragId}
          onRegisterDragHandlers={registerViewDragHandlers}
          exportKanbanColumnAsExcel={exportKanbanColumnAsExcel}
          droppedZoneId={droppedZoneId}
          showArchived={showArchived}
          showCompleted={showCompleted}
          isReadOnly={!isProjectEditor}
        />
      )}
    </>
  );
};

export default WorkspaceCollaborationRouter;
